import Dexie from 'dexie';
import { metaValue } from 'helpers/dom-helper';
import { FORM_STORE_NAME, deleteCopies, openFormStore, suspendAutoSave } from 'lib/form_store';
import ApplicationController from './application_controller';

// Deletes the signed-in person's autosave copies from this browser (lib/form_store.js). Patient portal pages use it,
// because parents often fill in intake documents on a shared device:
//
// - With `form-ids`, on connect: the page after a successful Save names the form that was saved, since a native
//   submit never reaches the autosave controller's own cleanup.
// - As `click->form-store-cleanup#clearThenFollow` on the sign-out link: the person's copies of every form whose id
//   starts with `form-id-prefix` (the portal's intake forms) are deleted, and then the link is followed. Their copies
//   of other forms stay: the same person may also autosave visit notes as staff on this browser.
export default class extends ApplicationController {
  static values = { formIds: Array, formIdPrefix: String };

  // A sign-out never waits longer than this on browser storage. Specs shorten it.
  static timeLimit = 2_000;

  async connect() {
    if (this.formIdsValue.length > 0) await this.clear({ formIds: this.formIdsValue });
  }

  async clearThenFollow(event) {
    // The second click is the one this handler makes below: let it through to the sign-out.
    if (this.following) return;

    event.preventDefault();
    event.stopPropagation();
    // First, so an autosave still running on these forms, on this page or in another tab, writes no copy after them.
    try {
      suspendAutoSave({ userId: metaValue('current-user-id'), formIdPrefix: this.formIdPrefixValue });
    } catch {
      // The copies are still deleted and the sign-out still happens.
    }
    try {
      await withinTime(this.clear({ formIdPrefix: this.formIdPrefixValue }), this.constructor.timeLimit);
    } catch {
      // Storage is unavailable or slow. Signing out matters more.
    }
    this.following = true;
    this.element.click();
  }

  async clear({ formIds = null, formIdPrefix = null } = {}) {
    const userId = metaValue('current-user-id');
    if (!userId) return 0;
    // Opening the store creates it, and a browser that never autosaved has nothing to delete.
    if (!(await Dexie.exists(FORM_STORE_NAME))) return 0;

    const db = openFormStore();
    try {
      return await deleteCopies(db, { userId, formIds, formIdPrefix });
    } finally {
      db.close();
    }
  }
}

function withinTime(promise, ms) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('timed out')), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}
